import axios from 'axios';
import { auth } from '../config/firebase';

// Definimos a URL base da sua API (Back-end)
// Se você mudou para 3001, ajuste aqui.
// const API_URL = 'http://localhost:3000/api';
// const API_URL = 'https://atendeflowbackend-production.up.railway.app/api';
// const API_URL = 'https://atendeflowbackend-dev.up.railway.app/api';
const API_URL = 'https://atendeflowbackend-production.up.railway.app/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Anexa o token do Firebase automaticamente em toda chamada quando há um usuário logado.
// Sem isso, cada uma das dezenas de chamadas abaixo precisaria buscar e passar o token na mão
// (como algumas já faziam) — e é exatamente a ausência disso que permitia o backend confiar
// cegamente no uid/usuarioId enviado no body/query em vez de validar quem está de fato logado.
api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Funções específicas para cada recurso
export const usuarioService = {
  // Rota: POST /api/usuarios/register
  registrar: (dados) => api.post('/usuarios/register', dados),

  // Rota: POST /api/usuarios/login (para quando criarmos)
  login: (credenciais) => api.post('/usuarios/login', credenciais),

  obterPerfil: (uid) => api.get(`/usuarios/perfil/${uid}`), // Rota GET
  atualizarPerfil: (uid, dados) => api.put(`/usuarios/perfil/${uid}`, dados), // Rota PUT

  recuperarSenha: (email) => api.post('/usuarios/recuperar-senha', { email }),
  sincronizarGoogle: (dados) => api.post('/usuarios/sync-google', dados),
  verificarOnboarding: (uid) => api.get(`/usuarios/onboarding-status/${uid}`),

  // Verifica se um slug (link público) está disponível. Passe o uid do usuário atual para ignorar o próprio slug.
  verificarSlugDisponivel: (slug, uid) => api.get(`/usuarios/slug-disponivel/${slug}`, { params: { uid } }),
};

export const googleService = {
  // Retorna a URL de autenticação do Google (agora aceita profissionalId)
  gerarUrlAuth: (usuarioId, profissionalId = null) => {
    const endpoint = profissionalId
      ? `/auth/google/auth?usuarioId=${usuarioId}&profissionalId=${profissionalId}`
      : `/auth/google/auth?usuarioId=${usuarioId}`;
    return api.get(endpoint);
  },

  // Verifica se o usuário já tem conta vinculada
  verificarStatus: (usuarioId) => api.get(`/auth/google/status/${usuarioId}`),

  // Remove o vínculo com a agenda (agora aceita profissionalId)
  desconectar: (usuarioId, profissionalId = null) => {
    const endpoint = profissionalId
      ? `/auth/google/disconnect/${usuarioId}?profissionalId=${profissionalId}`
      : `/auth/google/disconnect/${usuarioId}`;
    return api.delete(endpoint);
  },
};

export const agendaService = {
  // Chamada para o bot ou para criar manualmente via painel
  // POST /api/agenda/marcar/:usuarioUid
  agendar: (usuarioUid, dadosConsulta) => api.post(`/agenda/marcar/${usuarioUid}`, dadosConsulta),

  cancelar: (usuarioUid, dados) => api.post(`/agenda/cancelar/${usuarioUid}`, dados),
  editar: (usuarioUid, dados) => api.put(`/agenda/editar/${usuarioUid}`, dados),
  concluir: (usuarioUid, dados) => api.put(`/agenda/concluir/${usuarioUid}`, dados),
  reverterConclusao: (usuarioUid, dados) => api.put(`/agenda/reverter-conclusao/${usuarioUid}`, dados),
  // Chamada para o médico visualizar as consultas no Dashboard
  // GET /api/agenda/minhas-consultas/:usuarioUid
  listarMinhasConsultas: (usuarioUid) => api.get(`/agenda/minhas-consultas/${usuarioUid}`),

  // Horários livres pro agendamento manual no dashboard (mesmo cálculo do link público).
  // GET /api/agenda/horarios/:usuarioUid?servicos_ids=1,2&data=YYYY-MM-DD
  buscarHorarios: (usuarioUid, servicosIds, data) => api.get(`/agenda/horarios/${usuarioUid}`, {
    params: { servicos_ids: servicosIds.join(','), data }
  }),
};

export const configuracaoService = {
  // --- AGENDA (Passo 1) ---
  obterAgenda: (uid) => api.get(`/usuarios/configuracoes/agenda/${uid}`),
  salvarAgenda: (uid, dados) => api.post(`/usuarios/configuracoes/agenda/${uid}`, dados),

  // --- WHATSAPP (Passo 2) ---
  obterWhatsapp: (uid) => api.get(`/usuarios/configuracoes/whatsapp/${uid}`),
  salvarWhatsapp: (uid, dados) => api.post(`/usuarios/configuracoes/whatsapp/${uid}`, dados),

  // --- TEMA/CORES DA AGENDA PÚBLICA ---
  obterTema: (uid) => api.get(`/usuarios/configuracoes/tema/${uid}`),
  salvarTema: (uid, dados) => api.post(`/usuarios/configuracoes/tema/${uid}`, dados),

  // --- MARKETING: FIDELIDADE E ANIVERSÁRIO ---
  obterFidelidade: (uid) => api.get(`/usuarios/configuracoes/fidelidade/${uid}`),
  salvarFidelidade: (uid, dados) => api.post(`/usuarios/configuracoes/fidelidade/${uid}`, dados),
  obterAniversario: (uid) => api.get(`/usuarios/configuracoes/aniversario/${uid}`),
  salvarAniversario: (uid, dados) => api.post(`/usuarios/configuracoes/aniversario/${uid}`, dados),
};

export const whatsappService = {
  conectar: (uid, payload) => api.post(`/whatsapp/conectar/${uid}`, payload),
  // Token anexado automaticamente pelo interceptor em api.js — não precisa mais passar na mão
  reconectar: (instanceName, number) => api.post(`/whatsapp/reconectar`, { instanceName, number }),
  desconectar: (uid) => api.post(`/whatsapp/desconectar/${uid}`),
  obterStatus: (uid) => api.get(`/whatsapp/status/${uid}`),
  alternarStatusBot: (nomeInstancia, botAtivo) =>
    api.put(`/whatsapp/instancias/${nomeInstancia}/status-bot`, { bot_ativo: botAtivo }),
  // 👇 NOVA FUNÇÃO DE EXCLUSÃO ADICIONADA AQUI 👇
  excluirInstancia: (nomeInstancia) =>
    api.delete(`/whatsapp/instancias/${nomeInstancia}`),
};

export const dashboardService = {
  // GET /api/dashboard/:uid
  // Retorna os dados unificados: consultasHoje, statusWhatsapp, novosClientes, proximasConsultas
  obterResumo: (uid) => api.get(`/dashboard/${uid}`),
};

export const financeiroService = {
  // GET /api/financeiro/resumo/:uid?inicio=YYYY-MM-DD&fim=YYYY-MM-DD
  obterResumo: (uid, dataInicio, dataFim) => api.get(`/financeiro/resumo/${uid}`, { params: { inicio: dataInicio, fim: dataFim } }),
};

export const lembreteService = {
  // Rota: POST /api/lembretes
  agendar: (dados, token) => api.post(`/lembretes`, dados, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Rota: GET /api/lembretes
  listar: (token) => api.get(`/lembretes`, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Rota: POST /api/lembretes/em-massa
  agendarEmMassa: (dados, token) => api.post(`/lembretes/em-massa`, dados, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Rota: POST /api/lembretes/enviar-agora (envio imediato, sem agendar)
  enviarAgora: (dados, token) => api.post(`/lembretes/enviar-agora`, dados, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Modelos de mensagem salvos (reutilizáveis, por usuário)
  listarTemplates: (token) => api.get(`/lembretes/templates`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  criarTemplate: (dados, token) => api.post(`/lembretes/templates`, dados, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  excluirTemplate: (id, token) => api.delete(`/lembretes/templates/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

export const cupomService = {
  listar: (token) => api.get(`/cupons`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  criar: (dados, token) => api.post(`/cupons`, dados, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  atualizar: (id, dados, token) => api.put(`/cupons/${id}`, dados, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

export const clienteService = {
  // Rota: GET /api/clientes (lista com estatísticas de agendamentos/cancelamentos)
  listar: (token) => api.get(`/clientes`, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Rota: POST /api/clientes
  criar: (dados, token) => api.post(`/clientes`, dados, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Rota: PUT /api/clientes/:id
  atualizar: (id, dados, token) => api.put(`/clientes/${id}`, dados, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

export const pagamentoService = {
  // Chamada para Ativar 7 dias grátis
  ativarTrial: (dados, token) => api.post('/pagamento/ativar-trial', dados, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Chamada para Assinar Imediatamente
  assinarAgora: (dados, token) => api.post('/pagamento/assinar', dados, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Verifica se o usuário pode usar trial
  verificarTrial: (usuarioId, token) => api.get('/pagamento/verifica-trial', {
    params: { usuarioId }, // O Axios vai transformar isso em /historico?usuarioId=123
    headers: { Authorization: `Bearer ${token}` }
  }
  ),

  // Rota para cancelar
  cancelarAssinatura: (usuarioId, token) => api.post('/pagamento/cancelar',
    { usuarioId }, // Enviando no req.body
    { headers: { Authorization: `Bearer ${token}` } }
  ),

  // Listar histórico
  listarHistorico: (usuarioId, token) => api.get('/pagamento/historico', {
    params: { usuarioId }, // O Axios vai transformar isso em /historico?usuarioId=123
    headers: { Authorization: `Bearer ${token}` }
  })
};

export const servicoService = {
  // Criar um novo serviço (passando usuario_id no body)
  criar: (dados) => api.post('/servicos', dados),

  // Listar serviços do usuário
  listar: (usuarioId) => api.get(`/servicos/${usuarioId}`),

  // Atualizar serviço existente
  atualizar: (id, dados) => api.put(`/servicos/${id}`, dados),

  // Desativar serviço (passando usuario_id na query)
  desativar: (id, usuarioId) => api.delete(`/servicos/${id}?usuario_id=${usuarioId}`)
};

export const publicoService = {
  // Dados do estabelecimento pelo slug (link público de agendamento)
  obterPerfil: (slug) => api.get(`/publico/${slug}`),

  // Busca um cliente já cadastrado desse estabelecimento pelo telefone (null se for novo)
  buscarCliente: (slug, telefone) => api.get(`/publico/${slug}/cliente`, { params: { telefone } }),

  // Serviços ativos oferecidos por esse estabelecimento
  listarServicos: (slug) => api.get(`/publico/${slug}/servicos`),

  // Horários livres para um ou mais serviços em uma data específica (data no formato YYYY-MM-DD)
  buscarHorarios: (slug, servicosIds, data) => api.get(`/publico/${slug}/horarios`, {
    params: { servicos_ids: Array.isArray(servicosIds) ? servicosIds.join(',') : servicosIds, data }
  }),

  // Envia um código de confirmação por WhatsApp pro telefone informado
  enviarCodigo: (slug, dados) => api.post(`/publico/${slug}/enviar-codigo`, dados),

  // Confirma o código recebido por WhatsApp
  verificarCodigo: (slug, dados) => api.post(`/publico/${slug}/verificar-codigo`, dados),

  // Valida um cupom de desconto pro serviço escolhido
  validarCupom: (slug, dados) => api.post(`/publico/${slug}/validar-cupom`, dados),

  // Cria o agendamento (e o cadastro do cliente, se ainda não existir)
  agendar: (slug, dados) => api.post(`/publico/${slug}/agendar`, dados),

  // Lista os agendamentos futuros de um cliente pelo telefone (para ele escolher qual cancelar)
  meusAgendamentos: (slug, telefone) => api.get(`/publico/${slug}/meus-agendamentos`, { params: { telefone } }),

  // Cancela um agendamento específico (telefone + id, validado no back-end)
  cancelar: (slug, dados) => api.post(`/publico/${slug}/cancelar`, dados),
};

export const profissionalService = {
  // Criar profissional (espera objeto com {usuario_id, nome})
  criar: (dados) => api.post('/profissionais', dados),

  // Listar equipe da clínica
  listar: (usuarioId) => api.get(`/profissionais/${usuarioId}`),

  // Atualizar profissional
  atualizar: (id, dados) => api.put(`/profissionais/${id}`, dados),

  // Deletar profissional (enviando usuario_id no parametro data para rotas DELETE no axios)
  deletar: (id, usuarioId) => api.delete(`/profissionais/${id}`, { data: { usuario_id: usuarioId } })
};

// export const whatsappService = {
//   // Exemplo de futura rota para instâncias
//   listarInstancias: () => api.get('/whatsapp/instances'),
//   conectar: (instanceName) => api.post('/whatsapp/connect', { instanceName }),
// };

export default api;